import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useGetSet } from 'react-use';

import ArrowShapeUp from '@gravity-ui/icons/ArrowShapeUp';
import Xmark from '@gravity-ui/icons/Xmark';
import { Button as GravityButton, Checkbox, Icon, Label } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { skillTestApi } from 'client/api';
import { TestAudioItem } from 'client/blocks/TestChat/TestAudioItem';
import {
    GeoPoint,
    getKeyboardButtons,
    getPrettifiedHistory,
    initialSession,
    MissingInterfaceLabel,
    SendMessageParams,
    TestChatHistoryBlockProps,
} from 'client/blocks/TestChat/TestChat';
import { AliceAppTestGeo } from 'client/blocks-redesign/AliceAppTestPage/AliceAppTestGeo';
import { AliceAppTestPageHistoryHighlighter } from 'client/blocks-redesign/AliceAppTestPageHistoryHighlighter/AliceAppTestPageHistoryHighlighter';
import { useMessageCardsGetter } from 'client/hooks/TestChat/useMessageCardsGetter';
import { useTestAudioPlayer } from 'client/hooks/TestChat/useTestAudioPlayer';
import { assistantMessage, CARD_TYPES, History, userMessage } from 'client/lib/searchband';
import { getLeastCapableSurface, Interface } from 'client/lib/surfaces';
import { Channel } from 'client/model/skill';
import { initialSessionState, sessionStateReducer } from 'client/reducers/testChat/sessionState';
import {
    AudioPlaybackState,
    AudioPlayerEvent,
    AudioPlayerEventType,
    AudioPlayerState,
    Button,
    CardActionArgs,
    isAudioPlayerMessage,
    isErrorMessage,
    Message,
    PlayerControlPhrases,
    TextMessage,
    TextMessageButton,
    TextMessageSession,
} from 'client/types/skillTest';
import { openAccountLinkingWindow } from 'client/utils/accountLinking';
import { getErrorCode } from 'common/utils/errorsHandler';
import * as keyset from 'i18n/TestPage';

import styles from './AliceAppTestPage.module.scss';

const t = i18n(keyset);

interface Props {
    skillId: string;
    isDraft?: boolean;
}

const makeTextMessage = (text: string, backgroundColor?: string) => {
    const msg = assistantMessage({
        cards: [
            {
                type: CARD_TYPES.text,
                text,
            },
        ],
    });

    if (backgroundColor) {
        msg.backgroundColor = backgroundColor;
        msg.borderColor = 'transparent';
    }

    return msg;
};

const makeErrorMessage = (text: string) => {
    // --color-red-danger-light
    return makeTextMessage(text, 'rgba(255, 0, 61, 0.15)');
};

const makeServiceMessage = (text: string) => {
    // --g-color-private-green-100
    return makeTextMessage(text, 'rgba(50, 186, 118, 0.15)');
};

const TestChatHistoryBlock: React.FC<TestChatHistoryBlockProps> = ({ history }) => {
    const [request, response] = getPrettifiedHistory(history);

    return (
        <div>
            <AliceAppTestPageHistoryHighlighter data={response} placeholder="empty response" label="Response:" />
            <AliceAppTestPageHistoryHighlighter data={request} label="Request:" />
        </div>
    );
};

export const AliceAppTestChat: React.FC<Props> = ({ isDraft, skillId }) => {
    const [sessionState, dispatchSessionAction] = useReducer(sessionStateReducer, initialSessionState);
    const [isAnonymousUser, setIsAnonymousUser] = useState(false);
    const [supportedInterfaces, setSupportedInterfaces] = useState<Record<Interface, boolean>>({ screen: true });
    const { cardButtons } = useMessageCardsGetter();
    const sessionRef = useRef<TextMessageSession>(initialSession);
    const [isSessionStarted, setIsSessionStarted] = useState(false);
    const [isAudioError, setIsAudioError] = useState(false);
    const [delayedMessage, setDelayedMessage] = useState<Message>();

    const { isLoggedIn, buttons, messages, history } = sessionState;

    const [getSharingGeo, setSharingGeo] = useGetSet<GeoPoint | null>(null);

    const messagesRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const currentAudioPlayerStateRef = useRef<AudioPlayerState | undefined>();

    const unmounted = useRef(false);
    useEffect(
        () => () => {
            unmounted.current = true;
        },
        [],
    );
    const currentMessageText = useRef<string>('');

    const [showGeo, setShowGeo] = useState(false);

    const sendMessageUnhandled = useCallback(
        ({ text, payload, directives, audioPlayerEvent }: SendMessageParams) => {
            if (text) {
                const message = userMessage({ text });

                dispatchSessionAction({
                    type: 'Message',
                    payload: { message },
                });
            }

            const { id, seq } = sessionRef.current;
            const surface = getLeastCapableSurface(supportedInterfaces);
            const sharingGeo = getSharingGeo();
            const params = {
                skillId,
                channel: Channel.AliceSkill,
                text,
                isDraft: Boolean(isDraft),
                sessionId: id,
                sessionSeq: seq,
                payload,
                surface,
                directives,
                isAnonymousUser,
                // На каждый запрос в навык отправляем текущее состояние плеера
                audioPlayerState: currentAudioPlayerStateRef.current,
                audioPlayerEvent,
                ...(sharingGeo ? { location: sharingGeo } : {}),
            };

            return skillTestApi.sendMessage(params);
        },
        [supportedInterfaces, getSharingGeo, skillId, isDraft, isAnonymousUser],
    );

    const {
        audioEl,
        currentTrack,
        queue,
        onAudioResponse,
        init: initAudioPlayer,
        currentTime,
        duration,
        playbackState,
        isAudioMetaLoading,
        audioControls,
    } = useTestAudioPlayer({
        hooks: {
            onPlay: () =>
                sendAudioPlayerEvent({
                    type: AudioPlayerEventType.PlaybackStarted,
                }),
            onPause: () =>
                sendAudioPlayerEvent({
                    type: AudioPlayerEventType.PlaybackStopped,
                }),
            onFinish: async () => {
                void sendAudioPlayerEvent({
                    type: AudioPlayerEventType.PlaybackFinished,
                });

                if (delayedMessage) {
                    if (isErrorMessage(delayedMessage)) {
                        dispatchSessionAction({
                            type: 'ReplyErrorMessage',
                            payload: {
                                message: makeErrorMessage(delayedMessage.error),
                            },
                        });
                    } else {
                        dispatchSessionAction({
                            type: 'ReplyMessage',
                            payload: {
                                message: makeMessage(delayedMessage),
                                buttons: getKeyboardButtons(delayedMessage),
                                history: delayedMessage.history,
                                isLoggedIn: delayedMessage.isLoggedIn,
                            },
                        });
                    }

                    setDelayedMessage(undefined);
                }
            },
            onNearlyFinished: async () => {
                const message = await sendAudioPlayerEvent({
                    type: AudioPlayerEventType.PlaybackNearlyFinished,
                });

                if (unmounted.current) {
                    return;
                }

                setDelayedMessage(message);
            },
            onError: async (errorType, message) => {
                setIsAudioError(true);

                await sendAudioPlayerEvent({
                    type: AudioPlayerEventType.PlaybackFailed,
                    error: {
                        type: errorType,
                        message,
                    },
                });
            },
        },
    });

    useEffect(() => {
        setIsAudioError(false);
    }, [currentTrack]);

    useEffect(() => {
        if (currentTrack === null || playbackState === null) {
            return;
        }

        currentAudioPlayerStateRef.current = {
            token: currentTrack.token,
            state: playbackState,
            offset_ms: Math.floor(currentTime * 1000),
        };
    }, [currentTrack, playbackState, currentTime]);

    const isCurrentSessionMessage = useCallback((message: TextMessage) => {
        const isSubsequentMessage = sessionRef.current.id === message.session.id;
        const isInitialMessage = sessionRef.current.id === '' && message.session.seq === 0;

        return isSubsequentMessage || isInitialMessage;
    }, []);

    const sendAudioPlayerEvent = useCallback(
        async (event: AudioPlayerEvent) => {
            if (!__appConfig.skillTesting.audioPlayer.activeEvents[event.type]) {
                return;
            }

            dispatchSessionAction({
                type: 'Message',
                payload: {
                    message: makeServiceMessage(`${t('event')} ${event.type}`),
                },
            });

            const message = await sendMessageUnhandled({
                text: '',
                audioPlayerEvent: event,
            });

            if (unmounted.current) {
                return;
            }

            if (!isErrorMessage(message) && !isCurrentSessionMessage(message)) {
                return;
            }

            if (message.history !== undefined && event.type !== AudioPlayerEventType.PlaybackStarted) {
                dispatchSessionAction({
                    type: 'SetHistory',
                    payload: {
                        history: message.history,
                    },
                });
            }

            if (isAudioPlayerMessage(message)) {
                onAudioResponse(message.audioPlayerAction, event.type);
            }

            return message;
        },
        [sendMessageUnhandled, onAudioResponse, isCurrentSessionMessage],
    );

    useEffect(() => {
        void sendMessage({ text: '' });

        inputRef.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setTimeout(() => {
            const messagesEl = messagesRef.current;
            if (messagesEl) {
                messagesEl.scrollTop = messagesEl.scrollHeight;
            }
        });
    }, [messages, queue]);

    const makeMessage = useCallback((message: TextMessage) => assistantMessage(message.layout), []);

    const sendMessage = useCallback(
        async (params: SendMessageParams) => {
            try {
                const message = await sendMessageUnhandled(params);

                if (unmounted.current) {
                    return;
                }

                if (isErrorMessage(message)) {
                    dispatchSessionAction({
                        type: 'ReplyErrorMessage',
                        payload: {
                            message: makeErrorMessage(message.error),
                            history: message.history,
                        },
                    });

                    return;
                }

                if (!isCurrentSessionMessage(message)) {
                    return;
                }

                if (isAudioPlayerMessage(message)) {
                    onAudioResponse(message.audioPlayerAction);
                }

                const isSessionEnded = message.session.isEnded;

                sessionRef.current = isSessionEnded ? initialSession : message.session;

                setIsSessionStarted(!isSessionEnded);

                dispatchSessionAction({
                    type: 'ReplyMessage',
                    payload: {
                        message: makeMessage(message),
                        buttons: getKeyboardButtons(message),
                        history: message.history,
                        isLoggedIn: message.isLoggedIn,
                        trailingMessage: isSessionEnded ? makeServiceMessage('Сессия завершена') : undefined,
                    },
                });
            } catch (err) {
                console.error(err);

                let errorText = t('something-went-wrong');
                const errorCode = getErrorCode(err);

                if (errorCode && errorCode >= 500 && errorCode < 600) {
                    errorText = t('error-try-again');
                }

                if (errorCode && errorCode >= 400 && errorCode < 500) {
                    errorText = t('error-invalid-url');
                }

                dispatchSessionAction({
                    type: 'ReplyErrorMessage',
                    payload: {
                        message: makeErrorMessage(errorText),
                        history: { request: {}, response_raw: '' },
                    },
                });
            }
        },
        [sendMessageUnhandled, isCurrentSessionMessage, makeMessage, onAudioResponse],
    );

    const onSubmitGeo = useCallback(
        async (geo: GeoPoint) => {
            setShowGeo(false);
            setSharingGeo(geo);
            await sendMessage({ text: currentMessageText.current });
        },
        [sendMessage, setSharingGeo],
    );

    const onCloseGeo = React.useCallback(() => {
        setShowGeo(false);
    }, []);

    const onRemoveGeo = useCallback(() => {
        setSharingGeo(null);
    }, [setSharingGeo]);

    const toggleSupportedInterface = useCallback(
        (i: Interface) => {
            const isSupported = !supportedInterfaces[i];

            setSupportedInterfaces({
                ...supportedInterfaces,
                [i]: isSupported,
            });
        },
        [supportedInterfaces],
    );

    const logout = useCallback(async () => {
        const errorMessage = makeErrorMessage(t('fail-to-leave-skill'));

        try {
            const { result } = await skillTestApi.logoutFromSkill({
                channel: Channel.AliceSkill,
                isDraft: Boolean(isDraft),
                skillId,
            });

            if (unmounted.current) {
                return;
            }

            const message = result ? makeTextMessage(t('success-to-leave-skill')) : errorMessage;

            dispatchSessionAction({
                type: 'LogoutMessage',
                payload: {
                    isLoggedIn: !result,
                    message,
                },
            });
        } catch (_e) {
            dispatchSessionAction({
                type: 'LogoutMessage',
                payload: {
                    isLoggedIn,
                    message: errorMessage,
                },
            });
        }
    }, [isDraft, isLoggedIn, skillId]);

    const openAccountLinking = useCallback(
        (url: string) => {
            const onSuccess = async () => {
                const directive = {
                    type: 'server_action',
                    name: 'bass_action',
                    payload: {
                        name: 'external_skill__account_linking_complete',
                        data: {},
                    },
                };

                await sendMessage({ text: '', directives: [directive] });
            };

            const onFailure = () => {
                dispatchSessionAction({
                    type: 'Message',
                    payload: {
                        message: makeErrorMessage(t('auth-error-happened')),
                    },
                });
            };

            openAccountLinkingWindow({ url, onSuccess, onFailure });
        },
        [sendMessage],
    );

    const onButtonClick = useCallback(
        async (button: Button & TextMessageButton, hide = false) => {
            if (button.type === 'start_account_linking' && button.url) {
                openAccountLinking(button.url);
                return;
            }

            if (button.type === 'request_geosharing' && button.period_min) {
                setShowGeo(true);
                return;
            }

            if (button.url) {
                window.open(button.url);
            }

            if (hide) {
                dispatchSessionAction({ type: 'HideButtons' });
            }

            await sendMessage({ text: button.title ?? currentMessageText.current, payload: button.payload });
        },
        [openAccountLinking, sendMessage],
    );

    const handleMessageAction = useCallback(
        (arg: CardActionArgs) =>
            setTimeout(async () => {
                const { action } = arg;
                switch (action.type) {
                    case 'button': {
                        const directive = action.directives[0];
                        if (directive) {
                            return onButtonClick(directive, directive.hide);
                        }
                        return sendMessage({ text: currentMessageText.current });
                    }
                    case 'uri': {
                        const button = action.url ? cardButtons.current[action.url] : null;

                        if (button) {
                            await sendMessage({ text: button.text, payload: button.payload });
                        } else {
                            window.open(action.url);
                        }
                        break;
                    }
                    case 'dialog-action': {
                        const [button] = action.directives;
                        await sendMessage({ text: button.text, payload: button.payload });
                        break;
                    }
                }
            }),
        [cardButtons, onButtonClick, sendMessage],
    );

    const handleFormSubmit = useCallback(
        async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const message = inputRef.current?.value;

            if (message && inputRef.current) {
                inputRef.current.value = '';
                inputRef.current.focus();

                await sendMessage({ text: message });
            }
        },
        [sendMessage],
    );

    const handleFormReset = useCallback(async () => {
        dispatchSessionAction({ type: 'ResetSessionState' });
        setIsSessionStarted(false);
        sessionRef.current = initialSession;

        if (playbackState === AudioPlaybackState.PLAYING) {
            await sendAudioPlayerEvent({ type: AudioPlayerEventType.PlaybackStopped });
        }

        initAudioPlayer();
        currentAudioPlayerStateRef.current = undefined;
        // Инициализируем новую сессию
        await sendMessage({ text: '' });
    }, [initAudioPlayer, playbackState, sendAudioPlayerEvent, sendMessage]);

    const sharingGeo = getSharingGeo();

    return (
        <div className={styles.testChat}>
            <div className={styles.testChat__main}>
                <div className={styles.testChat__chatColumn}>
                    <h2 className={styles.testChat__header}>{t('header-chat')}</h2>
                    <div className={styles.testChat__chatComponents}>
                        <div className={styles.testChat__chat}>
                            <div
                                className={styles.testChat__messages}
                                ref={messagesRef}
                                onClick={(e) => {
                                    const target = e.target as HTMLElement;
                                    if (target && target.classList.contains('assistant__message__card__text__action')) {
                                        currentMessageText.current = target.textContent ?? '';
                                        e.preventDefault();
                                    }
                                }}
                            >
                                <History messages={messages} onAction={handleMessageAction} />
                            </div>
                            {buttons.length > 0 && (
                                <div className={styles.testChat__suggest}>
                                    {buttons.map((button, index) => (
                                        <button
                                            className={clx(
                                                styles.testChat__action,
                                                Boolean(button.url) && styles.testChat__action_external,
                                            )}
                                            key={index}
                                            onClick={() => onButtonClick(button, true)}
                                        >
                                            {button.title}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className={styles.testChat__toolbar}>
                                <form className={styles.testChat__form} onSubmit={handleFormSubmit}>
                                    <input
                                        className={styles.testChat__input}
                                        ref={inputRef}
                                        placeholder={t('input-placeholder')}
                                    />
                                    <GravityButton
                                        type="submit"
                                        title={t('button-label-send')}
                                        view="flat-secondary"
                                        pin="brick-round"
                                        size="xl"
                                    >
                                        <Icon data={ArrowShapeUp} size="14" />
                                    </GravityButton>
                                </form>

                                <div className={styles.testChat__actions}>
                                    {isSessionStarted && (
                                        <GravityButton
                                            onClick={handleFormReset}
                                            title={t('button-label-delete')}
                                            view="flat-danger"
                                            pin="brick-round"
                                            size="xl"
                                        >
                                            <Icon data={Xmark} size="14" />
                                        </GravityButton>
                                    )}
                                </div>
                            </div>
                        </div>
                        {currentTrack !== null && (
                            <div className={styles.testChat__player}>
                                <TestAudioItem
                                    track={currentTrack}
                                    currentTime={isAudioError ? 0 : currentTime}
                                    duration={isAudioError ? 0 : duration}
                                    isPlaying={playbackState === AudioPlaybackState.PLAYING}
                                    isLoading={isAudioMetaLoading}
                                    onPlay={() => {
                                        void sendMessage({ text: PlayerControlPhrases.Continue });
                                    }}
                                    onPause={() => {
                                        void audioControls.pause();
                                    }}
                                    onSeek={(newCurrentTime) => {
                                        audioControls.pause();
                                        audioControls.seek(newCurrentTime);
                                    }}
                                    onNextTrack={() => {
                                        void sendMessage({ text: PlayerControlPhrases.NextTrack });
                                    }}
                                    onPrevTrack={() => {
                                        void sendMessage({ text: PlayerControlPhrases.PrevTrack });
                                    }}
                                    onSeekEnd={() => audioControls.play()}
                                />
                                {queue.map((track, i) => {
                                    return (
                                        <TestAudioItem
                                            track={track}
                                            key={i}
                                            preload
                                            onPlay={() => {
                                                void sendMessage({ text: PlayerControlPhrases.NextTrack });
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        )}
                        {audioEl}
                    </div>
                </div>
                {history && (
                    <div className={styles.testChat__codeColumn}>
                        <h2 className={styles.testChat__header}>{t('header-last-request')}</h2>
                        <div className={styles.testChat__code}>
                            <TestChatHistoryBlock history={history} />
                        </div>
                    </div>
                )}
            </div>
            {showGeo && <AliceAppTestGeo onCancel={onCloseGeo} onSubmit={onSubmitGeo} />}
            <div className={styles.testChat__options}>
                <Checkbox
                    size="l"
                    name={Interface.Screen}
                    checked={!supportedInterfaces[Interface.Screen]}
                    onChange={() => toggleSupportedInterface(Interface.Screen)}
                >
                    {MissingInterfaceLabel[Interface.Screen]}
                </Checkbox>
                <Checkbox
                    size="l"
                    name="isAnonymousUser"
                    checked={isAnonymousUser}
                    onChange={() => setIsAnonymousUser(!isAnonymousUser)}
                >
                    {t('chat-menu-option-anonymous')}
                </Checkbox>
                {sharingGeo && (
                    <Label
                        value={`${sharingGeo.lat}, ${sharingGeo.lon}`}
                        onCloseClick={onRemoveGeo}
                        size="m"
                        theme="info"
                        type="close"
                    >
                        {t('chat-menu-geo')}
                    </Label>
                )}
                {isLoggedIn && (
                    <Label onCloseClick={logout} size="m" theme="success" type="close">
                        {t('chat-you-logged')}
                    </Label>
                )}
            </div>
        </div>
    );
};
